import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Activity,
  AlertCircle,
  ArrowLeftRight,
  CheckCircle2,
  Clock,
  Flame,
  Loader2,
  MessageCircle,
  PhoneCall,
  Search,
  Snowflake,
  XCircle,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCalls } from "@/features/calls/hooks";
import type { Call } from "@/features/calls/api";
import { cn } from "@/lib/utils";

const STATUS_STYLES: Record<
  string,
  { label: string; cls: string; Icon: typeof CheckCircle2 }
> = {
  completed: {
    label: "Completed",
    cls: "text-emerald-600 bg-emerald-50 dark:bg-emerald-950/40 dark:text-emerald-400",
    Icon: CheckCircle2,
  },
  failed: {
    label: "Failed",
    cls: "text-rose-600 bg-rose-50 dark:bg-rose-950/40 dark:text-rose-400",
    Icon: XCircle,
  },
  no_answer: {
    label: "No answer",
    cls: "text-amber-600 bg-amber-50 dark:bg-amber-950/40 dark:text-amber-400",
    Icon: AlertCircle,
  },
  in_progress: {
    label: "In progress",
    cls: "text-blue-600 bg-blue-50 dark:bg-blue-950/40 dark:text-blue-400",
    Icon: Activity,
  },
  pending: {
    label: "Pending",
    cls: "text-slate-600 bg-slate-100 dark:bg-slate-800 dark:text-slate-300",
    Icon: Clock,
  },
};

function StatusBadge({ status }: { status: string }) {
  const entry =
    STATUS_STYLES[status] ?? {
      label: status,
      cls: "text-muted-foreground bg-muted",
      Icon: Activity,
    };
  const Icon = entry.Icon;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
        entry.cls,
      )}
    >
      <Icon className="size-3" />
      {entry.label}
    </span>
  );
}

const SENTIMENT_STYLES: Record<string, { emoji: string; cls: string }> = {
  positive: {
    emoji: "😊",
    cls: "text-emerald-600 border-emerald-200 dark:border-emerald-900 dark:text-emerald-400",
  },
  neutral: {
    emoji: "😐",
    cls: "text-muted-foreground border-input",
  },
  negative: {
    emoji: "😞",
    cls: "text-rose-600 border-rose-200 dark:border-rose-900 dark:text-rose-400",
  },
};

function LeadScoreChip({
  score,
  grade,
  status,
}: {
  score?: number | null;
  grade?: string;
  status?: string;
}) {
  if (status === "processing") {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
        <Loader2 className="size-3 animate-spin" />
        Scoring…
      </span>
    );
  }
  if (status === "disabled") {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  if (score == null || score === undefined) {
    return <span className="text-muted-foreground">—</span>;
  }
  if (grade === "hot") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 text-xs font-semibold text-rose-700 dark:bg-rose-950/40 dark:text-rose-300">
        <Flame className="size-3" />
        {score}
      </span>
    );
  }
  if (grade === "warm") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700 dark:bg-amber-950/40 dark:text-amber-300">
        {score}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">
      <Snowflake className="size-3" />
      {score}
    </span>
  );
}

function SentimentChip({ sentiment }: { sentiment?: string | null }) {
  if (!sentiment) return <span className="text-muted-foreground">—</span>;
  const s = SENTIMENT_STYLES[sentiment];
  if (!s) return <span>{sentiment}</span>;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs",
        s.cls,
      )}
    >
      <span>{s.emoji}</span>
      <span className="capitalize">{sentiment}</span>
    </span>
  );
}

function fmtDuration(seconds: number | null | undefined) {
  const val = seconds ?? 0;
  const m = Math.floor(val / 60);
  const s = val % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function fmtDate(iso?: string | null) {
  if (!iso) return "—";
  const t = Date.parse(iso);
  if (!Number.isFinite(t) || t === 0) return "—";
  return new Date(t).toLocaleString();
}

function callDate(call: Call) {
  return call.started_at || call.created_at || null;
}

const PAGE_OPTIONS = [25, 50, 100] as const;

export default function CallsPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [gradeFilter, setGradeFilter] = useState<string>("all");
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState<number>(25);

  const q = useCalls({
    ...(statusFilter !== "all" && { status: statusFilter }),
    ...(gradeFilter !== "all" && {
      lead_grade: gradeFilter as "hot" | "warm" | "cold",
    }),
  });

  const all = q.data ?? [];
  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return all;
    return all.filter(
      (c) =>
        (c.from_number || "").includes(s) ||
        (c.to_number || "").includes(s) ||
        (c.agent_name || "").toLowerCase().includes(s) ||
        (c.assistant_name || "").toLowerCase().includes(s) ||
        (c.agent?.name || "").toLowerCase().includes(s),
    );
  }, [all, search]);

  const start = page * perPage;
  const visible = filtered.slice(start, start + perPage);
  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Calls</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          View and manage all your voice calls.
        </p>
      </div>

      <Card>
        <CardContent className="flex flex-col gap-2 p-3 sm:flex-row">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              placeholder="Search by number or agent…"
              className="pl-8"
            />
          </div>
          <Select
            value={statusFilter}
            onValueChange={(v) => {
              setStatusFilter(v);
              setPage(0);
            }}
          >
            <SelectTrigger className="sm:w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="no_answer">No answer</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={gradeFilter}
            onValueChange={(v) => {
              setGradeFilter(v);
              setPage(0);
            }}
          >
            <SelectTrigger className="sm:w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All leads</SelectItem>
              <SelectItem value="hot">🔥 Hot</SelectItem>
              <SelectItem value="warm">Warm</SelectItem>
              <SelectItem value="cold">Cold</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 font-medium">From</th>
                  <th className="px-4 py-3 font-medium">To</th>
                  <th className="px-4 py-3 font-medium">Direction</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Agent / Assistant</th>
                  <th className="px-4 py-3 font-medium">Duration</th>
                  <th className="px-4 py-3 font-medium">Lead</th>
                  <th className="px-4 py-3 font-medium">Sentiment</th>
                  <th className="px-4 py-3 font-medium">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {q.isLoading &&
                  Array.from({ length: 6 }).map((_, i) => (
                    <tr key={i}>
                      <td colSpan={9} className="px-4 py-2">
                        <Skeleton className="h-6 w-full" />
                      </td>
                    </tr>
                  ))}

                {!q.isLoading && q.isError && (
                  <tr>
                    <td colSpan={9} className="px-4 py-12 text-center text-sm">
                      <AlertCircle className="mx-auto mb-2 size-6 text-rose-500" />
                      <p className="text-muted-foreground">
                        Couldn't load calls. The server may be busy.
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-3"
                        onClick={() => q.refetch()}
                        disabled={q.isFetching}
                      >
                        {q.isFetching ? (
                          <>
                            <Loader2 className="size-3.5 animate-spin" />
                            Retrying…
                          </>
                        ) : (
                          "Retry"
                        )}
                      </Button>
                    </td>
                  </tr>
                )}

                {!q.isLoading && !q.isError && visible.length === 0 && (
                  <tr>
                    <td
                      colSpan={9}
                      className="px-4 py-12 text-center text-sm text-muted-foreground"
                    >
                      <PhoneCall className="mx-auto mb-1 size-6 opacity-50" />
                      No calls found
                    </td>
                  </tr>
                )}

                {!q.isLoading && !q.isError &&
                  visible.map((c) => (
                    <tr
                      key={c.id}
                      className="cursor-pointer hover:bg-accent/40"
                      onClick={() => navigate(`/calls/${c.id}`)}
                    >
                      <td className="px-4 py-2.5">
                        <span className="inline-flex items-center gap-1.5">
                          {c.channel === "whatsapp" && (
                            <MessageCircle
                              className="size-3.5 shrink-0 text-emerald-600"
                              aria-label="WhatsApp call"
                            />
                          )}
                          {c.from_number || "—"}
                        </span>
                      </td>
                      <td className="px-4 py-2.5">{c.to_number || "—"}</td>
                      <td className="px-4 py-2.5">
                        <span
                          className={cn(
                            "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium capitalize",
                            c.direction === "inbound"
                              ? "text-blue-600 bg-blue-50 dark:bg-blue-950/40 dark:text-blue-400"
                              : c.direction === "outbound"
                              ? "text-purple-600 bg-purple-50 dark:bg-purple-950/40 dark:text-purple-400"
                              : "text-muted-foreground bg-muted",
                          )}
                        >
                          <ArrowLeftRight className="size-3" />
                          {c.direction || "—"}
                        </span>
                        {c.api_key_name && (
                          <span
                            title="Placed by an outside app with this API key"
                            className="ml-1.5 rounded-full bg-sky-50 px-1.5 py-0.5 text-[10px] font-medium text-sky-700 dark:bg-sky-950/40 dark:text-sky-400"
                          >
                            API · {c.api_key_name}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2.5">
                        <StatusBadge status={c.status} />
                      </td>
                      <td className="px-4 py-2.5 text-muted-foreground">
                        {c.assistant_name ? (
                          <span className="inline-flex items-center gap-1.5">
                            {c.assistant_name}
                            <span className="rounded-full bg-violet-50 px-1.5 py-0.5 text-[10px] font-medium text-violet-600 dark:bg-violet-950/40 dark:text-violet-400">
                              Assistant
                            </span>
                          </span>
                        ) : (
                          c.agent_name || c.agent?.name || "—"
                        )}
                      </td>
                      <td className="px-4 py-2.5 tabular-nums text-muted-foreground">
                        {fmtDuration(c.duration_seconds)}
                      </td>
                      <td className="px-4 py-2.5">
                        <LeadScoreChip
                          score={c.lead_score}
                          grade={c.lead_grade}
                          status={c.lead_score_status}
                        />
                      </td>
                      <td className="px-4 py-2.5">
                        <SentimentChip sentiment={c.sentiment} />
                      </td>
                      <td className="px-4 py-2.5 text-muted-foreground">
                        {fmtDate(callDate(c))}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 border-t px-4 py-2 text-xs text-muted-foreground">
            <div className="flex items-center gap-2">
              <span>Rows:</span>
              <Select
                value={String(perPage)}
                onValueChange={(v) => {
                  setPerPage(Number(v));
                  setPage(0);
                }}
              >
                <SelectTrigger className="h-7 w-20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_OPTIONS.map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      {n}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span>
                {filtered.length === 0
                  ? "0"
                  : `${start + 1}–${Math.min(start + perPage, filtered.length)}`}{" "}
                of {filtered.length}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                Prev
              </Button>
              <span className="tabular-nums">
                {page + 1} / {totalPages}
              </span>
              <Button
                variant="ghost"
                size="sm"
                disabled={page >= totalPages - 1}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
